import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Camera, Calendar, MapPin, AlertCircle, ChevronLeft, ChevronRight, 
  X, Download, Columns
} from 'lucide-react';
import jobPhotoService from '../services/jobPhotoService';

/**
 * SharedPhotos - Public page for customers to view shared job photos
 * No authentication required
 * 
 * Features:
 * - Multi-pair Before/After comparison (uses explicit pairing, falls back to upload order)
 * - Photo lightbox with keyboard navigation
 * - Download capability
 */
const SharedPhotos = () => {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'compare'
  const [currentPairIndex, setCurrentPairIndex] = useState(0);

  useEffect(() => {
    loadSharedPhotos();
  }, [shareToken]);

  const loadSharedPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await jobPhotoService.getSharedPhotos(shareToken);
      setData(response.data);
    } catch (err) {
      console.error('Error loading shared photos:', err);
      const status = err.response?.status;
      if (status === 404) {
        setError('This share link is invalid or not found.');
      } else if (status === 410) {
        setError(err.response?.data?.error || 'This share link has expired.');
      } else {
        setError('Unable to load photos. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Build Before/After pairs - use explicit pairing when available, fall back to upload order
  const comparisonPairs = useMemo(() => {
    if (!data?.photos) return [];
    
    const beforePhotos = data.photos
      .filter(p => p.type === 'Before')
      .sort((a, b) => new Date(a.createdAt || a.uploadedAt) - new Date(b.createdAt || b.uploadedAt));
    
    const afterPhotos = data.photos
      .filter(p => p.type === 'After')
      .sort((a, b) => new Date(a.createdAt || a.uploadedAt) - new Date(b.createdAt || b.uploadedAt));
    
    const pairs = [];
    const usedBeforeIds = new Set();
    
    // First, create pairs from explicit pairedWithPhotoId relationships
    afterPhotos.forEach(afterPhoto => {
      if (afterPhoto.pairedWithPhotoId) {
        const matchedBefore = beforePhotos.find(b => b.id === afterPhoto.pairedWithPhotoId);
        if (matchedBefore) {
          pairs.push({
            before: matchedBefore,
            after: afterPhoto,
            label: matchedBefore.caption || afterPhoto.caption || null
          });
          usedBeforeIds.add(matchedBefore.id);
        }
      }
    });
    
    // Then, pair remaining photos by upload order (legacy behavior)
    const unpairedBefore = beforePhotos.filter(b => !usedBeforeIds.has(b.id));
    const unpairedAfter = afterPhotos.filter(a => !a.pairedWithPhotoId);
    
    const maxUnpaired = Math.max(unpairedBefore.length, unpairedAfter.length);
    for (let i = 0; i < maxUnpaired; i++) {
      const before = unpairedBefore[i] || null;
      const after = unpairedAfter[i] || null;
      
      // Only add if we have at least one photo
      if (before || after) {
        pairs.push({
          before,
          after,
          label: before?.caption || after?.caption || null
        });
      }
    }
    
    // Add area numbers if multiple pairs and no custom labels
    if (pairs.length > 1) {
      pairs.forEach((pair, idx) => {
        if (!pair.label) {
          pair.label = `Area ${idx + 1}`;
        }
      });
    }
    
    return pairs;
  }, [data?.photos]);

  const hasBeforeAndAfter = comparisonPairs.length > 0 && 
    comparisonPairs.some(pair => pair.before && pair.after);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedPhotoIndex === null) return;
      
      switch (e.key) {
        case 'Escape':
          setSelectedPhotoIndex(null);
          break;
        case 'ArrowLeft':
          setSelectedPhotoIndex(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowRight':
          setSelectedPhotoIndex(prev => Math.min((data?.photos?.length || 1) - 1, prev + 1));
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhotoIndex, data?.photos?.length]);

  // Download single photo
  const handleDownload = async (photo) => {
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${photo.type || 'photo'}_${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading photo:', err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Camera className="mx-auto h-16 w-16 text-gray-300 animate-pulse" />
          <p className="mt-4 text-gray-500 font-medium">Loading photos...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="h-10 w-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to View Photos</h1>
          <p className="text-gray-600">{error}</p>
          <p className="text-sm text-gray-400 mt-4">
            If you believe this is an error, please contact the business that sent you this link.
          </p>
        </div>
      </div>
    );
  }

  const currentPair = comparisonPairs[currentPairIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header 
        className="bg-white shadow-sm border-b-4"
        style={{ borderBottomColor: data?.brandColor || '#3B82F6' }}
      >
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            {data?.businessLogo ? (
              <img 
                src={data.businessLogo} 
                alt={data.businessName || ''} 
                className="h-14 w-auto object-contain"
              />
            ) : (
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                style={{ backgroundColor: data?.brandColor || '#3B82F6' }}
              >
                {data?.businessName?.charAt(0) || 'S'}
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {data?.businessName || 'Job Photos'}
              </h1>
              {data?.jobAddress && (
                <p className="text-gray-600 flex items-center gap-1.5 mt-1">
                  <MapPin size={16} className="text-gray-400" />
                  {data.jobAddress}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Info Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <p className="text-gray-700 font-medium">
              {data?.photos?.length || 0} photo{data?.photos?.length !== 1 ? 's' : ''}
            </p>
            {data?.completedDate && (
              <p className="text-sm text-gray-500 flex items-center gap-1.5">
                <Calendar size={14} />
                Completed {new Date(data.completedDate).toLocaleDateString()}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Compare Toggle */}
            {hasBeforeAndAfter && (
              <button
                onClick={() => setViewMode(viewMode === 'compare' ? 'grid' : 'compare')}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                  viewMode === 'compare'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Columns size={16} />
                {viewMode === 'compare' ? 'Show All' : 'Compare'}
              </button>
            )}
          </div>
        </div>

        {/* Compare View - Multi-Pair Support */}
        {viewMode === 'compare' && hasBeforeAndAfter && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            {/* Pair Navigation (if multiple pairs) */}
            {comparisonPairs.length > 1 && (
              <div className="flex items-center justify-center gap-4 mb-6">
                <button
                  onClick={() => setCurrentPairIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentPairIndex === 0}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                
                <div className="flex items-center gap-2">
                  {comparisonPairs.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPairIndex(idx)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        idx === currentPairIndex ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={() => setCurrentPairIndex(prev => Math.min(comparisonPairs.length - 1, prev + 1))}
                  disabled={currentPairIndex === comparisonPairs.length - 1}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}

            {currentPair?.label && (
              <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                {currentPair.label}
              </h2>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Before */}
              <div>
                <div className="text-center mb-3">
                  <span className="inline-block px-4 py-1.5 bg-blue-500 text-white rounded-full text-sm font-medium">
                    Before
                  </span>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                  {currentPair?.before ? (
                    <img
                      src={currentPair.before.url}
                      alt="Before"
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => setSelectedPhotoIndex(
                        data.photos.findIndex(p => p.id === currentPair.before.id)
                      )}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No "Before" photo for this area
                    </div>
                  )}
                </div>
                {currentPair?.before?.caption && (
                  <p className="text-sm text-gray-600 text-center mt-2">{currentPair.before.caption}</p>
                )}
              </div>

              {/* After */}
              <div>
                <div className="text-center mb-3">
                  <span className="inline-block px-4 py-1.5 bg-green-500 text-white rounded-full text-sm font-medium">
                    After
                  </span>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                  {currentPair?.after ? (
                    <img
                      src={currentPair.after.url}
                      alt="After"
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => setSelectedPhotoIndex(
                        data.photos.findIndex(p => p.id === currentPair.after.id)
                      )}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No "After" photo for this area
                    </div>
                  )}
                </div>
                {currentPair?.after?.caption && (
                  <p className="text-sm text-gray-600 text-center mt-2">{currentPair.after.caption}</p>
                )}
              </div>
            </div>

            {/* Pair count indicator */}
            {comparisonPairs.length > 1 && (
              <p className="text-center text-sm text-gray-500 mt-4">
                Comparison {currentPairIndex + 1} of {comparisonPairs.length}
              </p>
            )}
          </div>
        )}

        {/* Photos Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {data?.photos?.map((photo, index) => (
            <button
              key={photo.id}
              onClick={() => setSelectedPhotoIndex(index)}
              className="group aspect-square rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-lg transition-all relative"
            >
              <img
                src={photo.thumbnailUrl || photo.url}
                alt={photo.caption || ''}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              {/* Type Badge */}
              {photo.type && (
                <div className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-medium shadow-sm ${
                  photo.type === 'Before' ? 'bg-blue-500 text-white' :
                  photo.type === 'After' ? 'bg-green-500 text-white' :
                  photo.type === 'Issue' ? 'bg-amber-500 text-white' :
                  photo.type === 'Progress' ? 'bg-purple-500 text-white' :
                  'bg-gray-500 text-white'
                }`}>
                  {photo.type}
                </div>
              )}
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <span className="text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  View
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Empty State */}
        {(!data?.photos || data.photos.length === 0) && (
          <div className="text-center py-16">
            <Camera className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">No photos available</p>
          </div>
        )}
      </main>

      {/* Photo Lightbox */}
      {selectedPhotoIndex !== null && data?.photos && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={() => setSelectedPhotoIndex(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors z-10"
          >
            <X size={28} />
          </button>

          {/* Download Button */}
          <button
            onClick={() => handleDownload(data.photos[selectedPhotoIndex])}
            className="absolute top-4 right-16 p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors z-10"
            title="Download"
          >
            <Download size={24} />
          </button>

          {/* Navigation */}
          {selectedPhotoIndex > 0 && (
            <button
              onClick={() => setSelectedPhotoIndex(prev => prev - 1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
            >
              <ChevronLeft size={32} />
            </button>
          )}
          {selectedPhotoIndex < data.photos.length - 1 && (
            <button
              onClick={() => setSelectedPhotoIndex(prev => prev + 1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
            >
              <ChevronRight size={32} />
            </button>
          )}

          {/* Main Image */}
          <div className="max-w-5xl max-h-[85vh] px-16">
            <img
              src={data.photos[selectedPhotoIndex].url}
              alt={data.photos[selectedPhotoIndex].caption || ''}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            
            {/* Photo Info */}
            <div className="mt-4 text-center">
              {data.photos[selectedPhotoIndex].type && (
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  data.photos[selectedPhotoIndex].type === 'Before' ? 'bg-blue-500 text-white' :
                  data.photos[selectedPhotoIndex].type === 'After' ? 'bg-green-500 text-white' :
                  data.photos[selectedPhotoIndex].type === 'Issue' ? 'bg-amber-500 text-white' :
                  data.photos[selectedPhotoIndex].type === 'Progress' ? 'bg-purple-500 text-white' :
                  'bg-gray-500 text-white'
                }`}>
                  {data.photos[selectedPhotoIndex].type}
                </span>
              )}
              {data.photos[selectedPhotoIndex].caption && (
                <p className="text-white mt-3">{data.photos[selectedPhotoIndex].caption}</p>
              )}
            </div>
          </div>

          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {selectedPhotoIndex + 1} of {data.photos.length}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 text-center border-t border-gray-200 bg-white mt-12">
        <p className="text-gray-500 text-sm">
          Photos shared by <span className="font-medium text-gray-700">{data?.businessName || 'SuddyFlow'}</span>
        </p>
        {data?.expiresAt && (
          <p className="text-xs text-gray-400 mt-2">
            This link expires {new Date(data.expiresAt).toLocaleDateString()}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-4">
          Powered by <span className="font-medium">SuddyFlow</span>
        </p>
      </footer>
    </div>
  );
};

export default SharedPhotos;